import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';

const AppConfigForm = ({ open, onClose, onSubmit, initialData, loading, title }) => {
  const [formData, setFormData] = useState({
    config_key: '',
    config_value: '',
    description: '',
  });

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          config_key: initialData.config_key || '',
          config_value: initialData.config_value || '',
          description: initialData.description || '',
        });
      } else {
        setFormData({
          config_key: '',
          config_value: '',
          description: '',
        });
      }
    }
  }, [initialData, open]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const isThemeConfig = initialData?.config_key === 'THEME' || formData.config_key === 'THEME';
  const isSchoolNameConfig = initialData?.config_key === 'SCHOOL_NAME' || formData.config_key === 'SCHOOL_NAME';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.config_key.trim() && !initialData) {
      return;
    }
    // When editing, only send value and description (not config_key)
    if (initialData) {
      const updateData = {
        config_value: formData.config_value,
        description: formData.description,
      };
      onSubmit(updateData);
    } else {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        config_key: '',
        config_value: '',
        description: '',
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title || (initialData ? 'Edit Configuration' : 'Add Configuration')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Configuration Key"
              value={formData.config_key}
              onChange={handleChange('config_key')}
              required
              disabled={!!initialData || loading}
              helperText={initialData ? "Configuration key cannot be changed" : "Unique identifier for this configuration (e.g., SCHOOL_NAME, THEME, CURRENT_SESSION)"}
              placeholder="e.g., SCHOOL_NAME"
            />
            
            {isThemeConfig ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    component="input"
                    type="color"
                    value={formData.config_value || '#115263'}
                    onChange={(e) => {
                      setFormData({ ...formData, config_value: e.target.value });
                    }}
                    sx={{
                      width: 60,
                      height: 40,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&::-webkit-color-swatch-wrapper': {
                        padding: 0,
                      },
                      '&::-webkit-color-swatch': {
                        border: 'none',
                        borderRadius: 1,
                      },
                    }}
                  />
                  <TextField
                    label="Theme Color (Hex)"
                    value={formData.config_value || ''}
                    onChange={handleChange('config_value')}
                    placeholder="#115263"
                    fullWidth
                    helperText="Select a color or enter a hex code (e.g., #115263)"
                    inputProps={{
                      pattern: '^#[0-9A-Fa-f]{6}$',
                      maxLength: 7,
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <TextField
                label="Configuration Value"
                value={formData.config_value}
                onChange={handleChange('config_value')}
                multiline={!isSchoolNameConfig}
                rows={isSchoolNameConfig ? 1 : 3}
                helperText={isSchoolNameConfig ? "Enter the school name" : "The value for this configuration"}
                placeholder={isSchoolNameConfig ? "e.g., St Thomas School" : "e.g., 2025-26"}
              />
            )}
            
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={2}
              helperText="Optional description of what this configuration is used for"
              placeholder={isThemeConfig ? "Primary theme color for the application" : isSchoolNameConfig ? "School name displayed in the application" : "e.g., Current academic session"}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading || (!initialData && !formData.config_key.trim())}>
            {loading ? <CircularProgress size={20} /> : initialData ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AppConfigForm;

